import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { flatMap as _flatMap, get as _get, has as _has } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import {
  addVehicleBindQuery,
  addVehicleInfoQuery,
  addVehicleQuoteQuery,
  typeOfAgentEmail
} from '@amfam/policy/add-vehicle/data-access';
import { TransformCoveragesPipe } from '@amfam/policy/add-vehicle/ui';
import { PlautoService } from '@amfam/policy/data-access';
import { AutoPolicy, ICoverage, PolicyCoverage } from '@amfam/policy/models';
import { TermPipe } from '@amfam/policy/pipes';
import { PolicySharedService } from '@amfam/policy/shared';
import { VehicleCoveragesFacade } from '@amfam/policy/vehicle-coverages/data-access';
import { vehicleQuery } from '@amfam/policy/vehicle/data-access';
import { QuoteModel, QuoteSummaryModel } from '@amfam/shared/models';
import { CoverageCurrencyUSDPipe } from '@amfam/shared/ui/pipes';
import { userQuery } from '@amfam/shared/user';
import { Agent, agentQuery } from '@amfam/shared/utility/agent';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService, UtilService } from '@amfam/shared/utility/shared-services';
import { DsModalService, LoadingSpinnerService } from '@amfam/ui-kit';

import * as VehicleInfoActions from '../+state/add-vehicle-feature.actions';
import * as addVehicleFeatureSelector from '../+state/add-vehicle-feature.selectors';
import { CoveragesModel } from '../models/coverages.model';
import {
  CoverageDescriptionModel,
  CoverageDescriptionModelForPartnerSpecific
} from '../models/vehicle-info.model';
import { AddVehicleFeatureService } from '../services/add-vehicle-feature.service';

@Component({
  selector: 'ds-vehicle-quote-summmary',
  templateUrl: './vehicle-quote-summmary.component.html',
  styleUrls: ['./vehicle-quote-summmary.component.scss'],
  standalone: false
})
export class VehicleQuoteSummmaryComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() cancelEvent = new EventEmitter<boolean>();

  @Output() errorEvent = new EventEmitter<unknown>();

  @Output() notifyAgentEvent = new EventEmitter<{ agent: Agent }>();

  public authorized$ = this.roleService.isAuthorized('addvehicle_sendtoagent');

  public isPartner$ = this.store.select(BrandSelectors.selectIsPartner);
  public vehicleId: string;

  public discountsOnCoveragesSummaryList$: Observable<QuoteSummaryModel[]> = this.store
    .select(addVehicleQuoteQuery.selectNewVehicleAppliedDiscounts)
    .pipe(
      map(appliedDiscounts =>
        _flatMap(appliedDiscounts, appliedDiscount => ({
          option:
            appliedDiscount.name.replace(/Discount/, '') +
            (this.isAntiTheftDiscountCode(appliedDiscount) ? '*' : ''),
          value: 'Applied'
        }))
      )
    );
  public vehicleCoveragesSummaryListPartners$: Observable<QuoteSummaryModel[]> =
    this.vehicleCoverageFacade.coverages$.pipe(
      map(c => new TransformCoveragesPipe().transform(c)),
      map(this.coverageReducer)
    );

  public policyCoveragesSummaryListPartners$: Observable<QuoteSummaryModel[]> =
    this.vehicleCoverageFacade.policyCoverages$.pipe(map(this.coverageReducer));

  public discountsOnCoveragesSummaryListPartners$: Observable<QuoteSummaryModel[]> =
    this.vehicleCoverageFacade.appliedDiscounts$.pipe(
      map(discounts =>
        discounts.map(d => ({
          option: d.name.replace(/Discount/, ''),
          value: 'Applied'
        }))
      )
    );

  public hasAntiTheftDiscount$: Observable<boolean> = this.store
    .select(addVehicleQuoteQuery.selectNewVehicleAppliedDiscounts)
    .pipe(
      map(
        appliedDiscounts =>
          !!appliedDiscounts.find(appliedDiscount => this.isAntiTheftDiscountCode(appliedDiscount))
      )
    );

  public vehicleRiskDescription$ = this.store.select(vehicleQuery.selectNewRiskDescription);
  public quoteDataForHeaderPartners$ = this.vehicleCoverageFacade.rate$.pipe(
    filter(quote => !!quote),
    map(
      (quoteRate): QuoteModel => ({
        quoteMonthlyDollars: this.utilService.returnQuoteDollarOrCents(
          +quoteRate.monthlyPremium,
          'Dollar'
        ),
        quoteMonthlyCents: this.utilService.returnQuoteDollarOrCents(
          +quoteRate.monthlyPremium,
          'Cent'
        ),
        quoteTerm: quoteRate.termMonths,
        quoteTotalAmount: Number(quoteRate.totalCost.toFixed(2))
      })
    )
  );

  public quoteDataForHeader$ = this.store.select(addVehicleQuoteQuery.selectNewVehicleRate).pipe(
    filter(quote => !!quote),
    map(
      (quoteRate): QuoteModel => ({
        quoteMonthlyDollars: this.utilService.returnQuoteDollarOrCents(
          quoteRate.monthlyPremium,
          'Dollar'
        ),
        quoteMonthlyCents: this.utilService.returnQuoteDollarOrCents(
          quoteRate.monthlyPremium,
          'Cent'
        ),
        quoteTerm: quoteRate.termMonths,
        quoteTotalAmount: Number(quoteRate.totalCost.toFixed(2))
      })
    )
  );

  public quoteSummaryForm: UntypedFormGroup = this.formBuilder.group({});

  public includedCoveragesHeader = this.copyService.getCopy(
    'policy.addVehicleIncludedCoverageHeader'
  );

  public discountsHeader = this.copyService.getCopy('policy.addVehicleDiscountsHeader');

  public agentSelectionModalId = this.modalService.createModalId();

  public agents$ = this.store.select(agentQuery.getRealAgents);

  public sendToAgentBtnText = this.copyService.getCopy('policy.newVehicleSendToAgent');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public newVehicleRate$: Observable<any> = this.store.select(
    addVehicleQuoteQuery.selectNewVehicleRate
  );

  private hasCostcoExecutiveMembership$ = this.store.select(
    addVehicleInfoQuery.selectHasCostcoExecutiveMembership
  );
  // eslint-disable-next-line @typescript-eslint/member-ordering
  public vehicleCoveragesSummaryList$: Observable<QuoteSummaryModel[]> = this.store
    .select(addVehicleQuoteQuery.selectNewVehicleCoverages)
    .pipe(
      withLatestFrom(this.store.select(userQuery.selectUserStateCode), this.isPartner$),
      map(([coverages, stateCode, isPartner]) => {
        const updatedCoverages = [...coverages];
        const coverageCodeListFromCopy = Object.keys(this.coverageCopyObjList);
        const coverageCodeListFromData = _flatMap(coverages, coverage => coverage.publicID);
        for (const copyCoverageCode of coverageCodeListFromCopy) {
          if (!coverageCodeListFromData.find(coverageCode => coverageCode === copyCoverageCode)) {
            updatedCoverages.push({
              coverageCode: copyCoverageCode,
              coverageName: '',
              termValue: '',
              selected: false,
              required: false,
              hasTerms: false,
              terms: []
            });
          }
        }

        return (
          updatedCoverages
            .filter(coverage => {
              const coverageCopyObj = this.coverageCopyObjList[_get(coverage, 'publicID')];
              const coverageDescriptionObjPartner =
                this.coverageCopyObjListForPartnerSpecific[_get(coverage, 'publicID')];
              const eligibleStates = _get(coverageDescriptionObjPartner, 'eligibleStates', []);
              const eligbleForCoverageInState =
                isPartner && eligibleStates.length > 0
                  ? !!eligibleStates.find(eligibleState => eligibleState === stateCode)
                  : true;
              const inEligibleStates = _get(coverageDescriptionObjPartner, 'inEligibleStates', []);
              const inEligibleForCoverageState =
                isPartner && inEligibleStates.length > 0
                  ? !!inEligibleStates.find(inEligibleState => inEligibleState === stateCode)
                  : false;
              return (
                (isPartner ? !!coverageDescriptionObjPartner : !!coverageCopyObj) &&
                eligbleForCoverageInState &&
                !inEligibleForCoverageState
              );
            })
            // .filter(
            //   coverage =>
            //     _get(coverage, 'publicID') !== 'PAEmergencyRoadServiceCov_af' &&
            //     _get(coverage, 'publicID') !== 'PATripTravelCov_af'
            // )
            .map((coverage): QuoteSummaryModel => {
              const coverageDescriptionObjPartner =
                this.coverageCopyObjListForPartnerSpecific[_get(coverage, 'publicID')];
              const description = _get(coverageDescriptionObjPartner, `description.${stateCode}`)
                ? _get(coverageDescriptionObjPartner, `description.${stateCode}`)
                : _get(coverageDescriptionObjPartner, 'description.Default');

              const coverageDescriptionObj = this.coverageCopyObjList[_get(coverage, 'publicID')];
              const coverageOptions = _flatMap(_get(coverage, 'terms', []), term => ({
                options: _flatMap(_get(term, 'options'), option => option.name),
                selectedValue: _get(term, 'chosenTermValue') || ''
              }));
              return {
                option:
                  isPartner && description
                    ? description
                    : _get(coverageDescriptionObj, 'description'),
                value: this.policySharedService.isCheckBox(coverage as unknown as ICoverage)
                  ? coverage.terms.length > 0 && coverage.selected
                    ? this.policySharedService.getSelectedValue(
                        coverage as unknown as ICoverage,
                        isPartner,
                        stateCode
                      )
                    : coverage.selected ||
                        (coverage.terms.length && coverage.terms[0].chosenTermValue)
                      ? 'Included'
                      : 'Not Included'
                  : this.getCoverageValue(
                      coverageOptions,
                      isPartner ? coverageDescriptionObjPartner : coverageDescriptionObj,
                      coverage.publicID,
                      isPartner
                    ),
                /*!!_get(
                  isPartner ? coverageDescriptionObjPartner : coverageDescriptionObj,
                  'isCheckBox'
                ) && coverage.selected
                  ? 'Included'
                  : this.getCoverageValue(
                      coverageOptions,
                      isPartner ? coverageDescriptionObjPartner : coverageDescriptionObj,
                      coverage.publicID,
                      isPartner
                    )*/ isCheckbox: !!_get(
                  isPartner ? coverageDescriptionObjPartner : coverageDescriptionObj,
                  'isCheckBox'
                ),
                coverageHelpTextList: this.handleUnderInsuredDeductibleHelpText(coverage)
              };
            })
            .filter(coverage => coverage.value)
            .sort((x, y) => (x.isCheckbox === y.isCheckbox ? 0 : x.isCheckbox ? 1 : -1))
        );
      })
    );

  // TODO: rv_premium Reference
  // eslint-disable-next-line @typescript-eslint/member-ordering
  public policyCoveragesSummaryList$: Observable<QuoteSummaryModel[]> = this.store
    .select(addVehicleFeatureSelector.selectCurrentAutoPolicy)
    .pipe(
      map(currentAutoPolicy =>
        this.autoPolicyService.getPolicyLevelCoverages(<AutoPolicy>currentAutoPolicy)
      ),
      withLatestFrom(
        this.hasCostcoExecutiveMembership$,
        this.isPartner$,
        this.store.select(userQuery.selectUserStateCode)
      ),
      map(([coverages, hasCostcoExecutiveMembership, isPartner, stateCode]) => {
        const coverageModels: QuoteSummaryModel[] = [];
        const additionalCoverages: string[] = [];
        for (const coverage of coverages) {
          if (
            _get(coverage, 'limitTerms[0].value') !== '' ||
            _get(coverage, 'deductibleTerms[0].value') !== ''
          ) {
            const coverageModel = new CoveragesModel(
              coverage,
              (<Dictionary<CoverageDescriptionModel>>(
                this.copyService.getCopy('policy.policyCoverageCodeMap')
              ))[coverage.code].subHeading
            );
            if (coverageModel.isVisible) {
              coverageModels.push({
                option: coverageModel.coverage.description,
                value: this.getPolicyCoverageLimits(coverageModel)
              });
            }
          } else if (
            _get(coverage, 'otherTerms[0].valueCode') !== 'Rejected' &&
            !_get(coverage, 'rejectedCoverage')
          ) {
            // Only display additional coverages if customer has not Rejected them
            additionalCoverages.push(coverage.description);
          }
        }

        if (isPartner && hasCostcoExecutiveMembership) {
          const coverageDescriptionObj =
            this.coverageCopyObjListForPartnerSpecific['PAEmergencyRoadServiceCov_af'];
          const description = _get(coverageDescriptionObj, `description.${stateCode}`)
            ? _get(coverageDescriptionObj, `description.${stateCode}`)
            : _get(coverageDescriptionObj, 'description.Default');
          coverageModels.push({
            option: description,
            value: 'Included'
          });
        } else {
          [
            this.coverageCopyObjList['PATripTravelCov_af'],
            this.coverageCopyObjList['PAEmergencyRoadServiceCov_af']
          ].forEach(cov => {
            coverageModels.push({
              option: cov.description,
              value: 'Included'
            });
          });
        }

        return coverageModels.sort((a, b) => a.option.localeCompare(b.option));
      })
    );

  private coverageCopyObjList = <Dictionary<CoverageDescriptionModel>>(
    this.copyService.getCopy('policy.vehicleCoverageCodeMap')
  );
  private coverageCopyObjListForPartnerSpecific = <
    Dictionary<CoverageDescriptionModelForPartnerSpecific>
  >this.copyService.getCopy('policy.vehicleCoverageCodeMapForPartners');

  private isOnlineBind: boolean;

  private stop$: Subject<void> = new Subject<void>();

  constructor(
    protected store: Store,
    protected route: ActivatedRoute,
    protected formBuilder: UntypedFormBuilder,
    protected spinnerService: LoadingSpinnerService,
    protected copyService: CopyService,
    protected autoPolicyService: PlautoService,
    protected modalService: DsModalService,
    protected addVehicleService: AddVehicleFeatureService,
    protected utilService: UtilService,
    protected roleService: ImpersonateRolesService,
    protected vehicleCoverageFacade: VehicleCoveragesFacade,
    protected policySharedService: PolicySharedService
  ) {}

  coverageReducer(coverages: ICoverage[] | PolicyCoverage[]) {
    return coverages.map(c => {
      let isCheckbox = false;
      let selectedValue = '';
      if (!c.hasTerms || (c.hasTerms && (c.terms.length < 1 || c.terms[0].options.length === 1))) {
        isCheckbox = true;
        selectedValue =
          c.selected || (c.terms.length && c.terms[0].chosenTermValue)
            ? 'Included'
            : 'Not Included';
      } else {
        if (c.selected) {
          selectedValue = new CoverageCurrencyUSDPipe().transform(
            c.terms[0].chosenTermValue.toString(),
            c.publicID
          );
        } else {
          selectedValue = 'Not Included';
        }
      }
      return {
        option: c.name,
        value: selectedValue,
        isCheckbox: isCheckbox,
        publicID: c.publicID,
        coverageHelpTextList: ['']
      };
    });
  }

  ngOnInit(): void {
    this.vehicleId = this.route.snapshot.paramMap.get('id');
    this.handleError();
    this.spinnerService.stop();
  }

  ngAfterViewInit() {
    this.store.dispatch(
      VehicleInfoActions.addVehicleSendAnalytics({
        payload: { pageAnalyticName: 'QuoteSummary' }
      })
    );
  }

  onlineBind() {
    this.spinnerService.start({
      blockActions: true
    });
    // this.vehicleCoverageFacade.bindChangeCoverage(this.vehicleId);
    this.agents$.pipe(withLatestFrom(this.isPartner$), take(1)).subscribe(([agents, isPartner]) => {
      if (agents.length > 1) {
        this.isOnlineBind = true;
        this.modalService.open(this.agentSelectionModalId);
      } else {
        if (isPartner) {
          this.bindWithAgentInfoForPartners(agents[0]);
        } else {
          this.bindWithAgentInfo(agents[0]);
        }
      }
    });
  }

  bindWithAgentInfoForPartners(agent: Agent) {
    combineLatest(
      this.vehicleCoverageFacade.workOrderNumberAndPolicyNumber$,
      this.addVehicleService.typeOfPartnerEmailBeingSent()
    )
      .pipe(
        filter(([vehicle, typeOfEmail]) => !!vehicle && !!typeOfEmail),
        take(1)
      )
      .subscribe(([vehicle, typeOfEmail]) => {
        this.store.dispatch(
          VehicleInfoActions.addVehicleBindQuote({
            payload: {
              agent,
              typeOfEmail,
              workOrderNumber: vehicle.workOrderNumber,
              vin: vehicle.vin
            }
          })
        );
      });
  }

  bindWithAgentInfo(agent: Agent) {
    combineLatest(
      this.store.select(addVehicleQuoteQuery.selectNewVehicleWorkOrderNumber),
      this.addVehicleService.typeOfAgentEmailBeingSent(),
      this.store.select(addVehicleQuoteQuery.selectNewVehicleVin)
    )
      .pipe(
        filter(
          ([workOrderNumber, typeOfEmail, vin]) => !!workOrderNumber && !!typeOfEmail && !!vin
        ),
        take(1)
      )
      .subscribe(([workOrderNumber, typeOfEmail, vin]) => {
        this.store.dispatch(
          VehicleInfoActions.addVehicleBindQuote({
            payload: { agent, typeOfEmail: typeOfEmail as typeOfAgentEmail, workOrderNumber, vin }
          })
        );
      });
  }

  agentConfirmationFromModal(agent: Agent) {
    if (this.isOnlineBind) {
      this.bindWithAgentInfo(agent);
    } else {
      this.notifyAgentEvent.emit({ agent });
    }
  }

  goBackOneStep() {
    this.store.dispatch(fromRouterActions.Back());
  }

  cancelEventFromAgentSelectionModal() {}

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  private getCoverageValue(
    coverageOptions: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      options: any[];
      selectedValue: string;
    }[],
    coverageDescriptionObj: CoverageDescriptionModel | CoverageDescriptionModelForPartnerSpecific,
    coverageCode: string,
    isPartner: boolean
  ) {
    if (_has(coverageDescriptionObj, 'isCheckBox')) {
      return _get(coverageOptions[0], 'selectedValue') ? 'Included' : 'Not Included';
    }

    const UMPDStateCode = !isPartner
      ? coverageDescriptionObj?.eligibleStates?.find(state => state === 'IL')
      : '';
    if (coverageOptions.length > 1) {
      return (
        this.getVehicleCoverageLimits(_get(coverageOptions[0], 'selectedValue'), coverageCode) +
        ', ' +
        this.getVehicleCoverageLimits(
          _get(coverageOptions[1], 'selectedValue'),
          coverageCode,
          UMPDStateCode
        )
      );
    }

    if (UMPDStateCode && !coverageOptions.length) {
      return '';
    }

    return this.getVehicleCoverageLimits(_get(coverageOptions[0], 'selectedValue'), coverageCode);
  }

  private getPolicyCoverageLimits(model: CoveragesModel) {
    let limit = '';
    for (const term of model.coverage.limitTerms) {
      limit = (limit ? `${limit}, ` : limit) + new TermPipe().transform(term);
    }
    if (!!model?.isStandardCoverage && !(model?.isImpliedCoverage || model?.isLiabilityCoverage)) {
      for (const term of model.coverage.deductibleTerms) {
        limit = (limit ? `${limit}, ` : limit) + new TermPipe().transform(term);
      }
    }

    return limit;
  }

  private getVehicleCoverageLimits(value: string, coverageCode: string, stateCode?: string) {
    if (!value) {
      return 'Not Included';
    }
    return new CoverageCurrencyUSDPipe().transform(value, coverageCode, stateCode);
  }

  private isAntiTheftDiscountCode(appliedDiscount) {
    return appliedDiscount.discountCode === 'PAAntiTheftDisc_af';
  }

  private handleError() {
    combineLatest([
      this.store.select(addVehicleBindQuery.selectVehicleANSNotificationError),
      this.store.select(addVehicleBindQuery.selectVehicleBindError)
    ])
      .pipe(
        map(([ansError, bindError]) => ({ ansError, bindError })),
        takeUntil(this.stop$)
      )
      .subscribe(({ ansError, bindError }) => {
        if (ansError || bindError) {
          this.errorEvent.emit();
          this.spinnerService.stop();
        }
      });
  }

  private handleUnderInsuredDeductibleHelpText(coverage) {
    const underInsuredHelpTextObj: string[] = [];

    for (const term of coverage.terms) {
      if (term.name === this.copyService.getCopy('policy.hit-and-RunPhantomVehicleDamage')) {
        underInsuredHelpTextObj.push(
          this.copyService.getCopy(
            `policy.vehicleCoverageCodeMap[${coverage.publicID}]['coverageHelpText1']`,
            {
              hitdeductible: term.chosenTermValue
            }
          )
        );
      } else if (
        term.name === this.copyService.getCopy('policy.notAHit-and-RunPhantomVehicleDamage')
      ) {
        underInsuredHelpTextObj.push(
          this.copyService.getCopy(
            `policy.vehicleCoverageCodeMap[${coverage.publicID}]['coverageHelpText2']`,
            {
              noHitdeductible: term.chosenTermValue
            }
          )
        );
      }
    }

    return underInsuredHelpTextObj;
  }
}
